import React, { useMemo, useState, useRef, useEffect } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import { makeStyles } from '@material-ui/core/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { UIIncident } from '../../../../../../../api/teamMetricsApi';

type SortDirection = 'asc' | 'desc';

interface TruncatedTextProps {
  text: string;
  children: React.ReactNode;
}

const TruncatedText: React.FC<TruncatedTextProps> = ({ text, children }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (element) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    }
  }, [text]);

  if (isTruncated) {
    return (
      <Tooltip title={text} placement="top-start">
        <span ref={ref} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {children}
        </span>
      </Tooltip>
    );
  }

  return (
    <span ref={ref} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {children}
    </span>
  );
};

const useStyles = makeStyles((theme) => ({
  container: {
    marginBottom: theme.spacing(4),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(3),
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
  },
  subtitle: {
    fontSize: 12,
    color: '#8E9299',
    marginTop: theme.spacing(0.5),
  },
  columnButton: {
    textTransform: 'none',
    fontWeight: 600,
    fontSize: 13,
    color: '#4A5568',
    borderColor: '#E2E8F0',
    '&:hover': {
      borderColor: '#CBD5E0',
      backgroundColor: '#F7FAFC',
    },
  },
  columnButtonIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: 18,
  },
  menuPaper: {
    maxHeight: 400,
    minWidth: 250,
  },
  menuHeader: {
    padding: theme.spacing(1, 2),
    fontSize: 11,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  menuItem: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  tableContainer: {
    border: '1px solid #E2E8F0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#F7FAFC',
  },
  headerCell: {
    fontSize: 10,
    fontWeight: 700,
    color: '#8E9299',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    borderBottom: '1px solid #E2E8F0',
    whiteSpace: 'nowrap',
  },
  sortLabel: {
    '&.MuiTableSortLabel-root': {
      color: '#8E9299',
      fontSize: 10,
      fontWeight: 700,
      textTransform: 'uppercase',
      letterSpacing: '0.1em',
    },
    '&.MuiTableSortLabel-root:hover': {
      color: '#4A5568',
    },
    '&.Mui-active': {
      color: '#1A1A1A',
    },
    '& .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#8E9299',
      fontSize: 14,
    },
    '&.Mui-active .MuiTableSortLabel-icon': {
      opacity: 1,
      color: '#1A1A1A',
    },
  },
  tableRow: {
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#F7FAFC',
    },
  },
  cell: {
    borderBottom: '1px solid #E2E8F0',
    fontSize: 13,
    color: '#4A5568',
    maxWidth: 250,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  descriptionCell: {
    maxWidth: 400,
  },
  linkText: {
    color: '#0070F3',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  incidentNameLink: {
    color: '#0070F3',
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  emptyState: {
    padding: theme.spacing(6),
    textAlign: 'center',
    color: '#8E9299',
  },
  emptyIcon: {
    fontSize: 48,
    color: '#CBD5E0',
    marginBottom: theme.spacing(2),
  },
  severityCell: {
    fontWeight: 600,
  },
  sev1: {
    color: '#E53E3E',
  },
  sev2: {
    color: '#ED8936',
  },
  sev3: {
    color: '#ECC94B',
  },
  sev4: {
    color: '#805AD5',
  },
}));

// Default columns aligned with Response Quality metrics:
// - Time to Detection (TTD): Time to Detection (calculated)
// - Detection Gap: Detection Method
// - Time to Response (TTR): Response Time (calculated)
// - MTTR by Severity: SEV Level V2
const DEFAULT_COLUMN_PATTERNS = [
  'description',
  'start date',
  'sev level v2',
  'time to detection',
  'detection method',
  'response time',
];

function normalizeColumnName(name: string): string {
  return name.toLowerCase().replace(/\n/g, ' ').replace(/\s+/g, ' ').trim();
}

function findMatchingColumn(available: string[], pattern: string): string | null {
  const normalizedPattern = normalizeColumnName(pattern);
  return available.find((col) => normalizeColumnName(col).startsWith(normalizedPattern)) || null;
}

function getDefaultColumns(available: string[]): string[] {
  const defaults: string[] = [];
  for (const pattern of DEFAULT_COLUMN_PATTERNS) {
    const match = findMatchingColumn(available, pattern);
    if (match && !defaults.includes(match)) {
      defaults.push(match);
    }
  }
  return defaults;
}

interface IncidentsTableProps {
  incidents: UIIncident[];
  columnHeaders: string[];
}

export const IncidentsTable: React.FC<IncidentsTableProps> = ({ incidents, columnHeaders }) => {
  const classes = useStyles();
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  const allAvailableColumns = useMemo(() => {
    if (columnHeaders.length > 0) {
      return columnHeaders;
    }
    if (incidents.length > 0) {
      const keysFromData = new Set<string>();
      incidents.forEach((inc) => {
        Object.keys(inc.fields).forEach((key) => keysFromData.add(key));
      });
      return Array.from(keysFromData);
    }
    return [];
  }, [columnHeaders, incidents]);

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    let available: string[] = [];
    if (columnHeaders.length > 0) {
      available = columnHeaders;
    } else if (incidents.length > 0) {
      available = Object.keys(incidents[0].fields);
    }
    const defaults = getDefaultColumns(available);
    if (defaults.length >= 3) {
      return defaults;
    }
    return available.slice(0, 7);
  });

  const handleColumnToggle = (column: string) => {
    setSelectedColumns((prev) => {
      if (prev.includes(column)) {
        return prev.filter((c) => c !== column);
      }
      return [...prev, column];
    });
  };

  const handleSelectAll = () => {
    setSelectedColumns(allAvailableColumns);
  };

  const handleSelectNone = () => {
    setSelectedColumns([]);
  };

  const handleResetToDefault = () => {
    const defaults = getDefaultColumns(allAvailableColumns);
    if (defaults.length >= 3) {
      setSelectedColumns(defaults);
    } else {
      setSelectedColumns(allAvailableColumns.slice(0, 7));
    }
  };

  const handleSort = (key: string) => {
    if (sortKey === key) {
      if (sortDirection === 'desc') {
        setSortDirection('asc');
      } else {
        setSortKey(null);
        setSortDirection('desc');
      }
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  const sortedIncidents = useMemo(() => {
    if (!sortKey) return incidents;

    return [...incidents].sort((a, b) => {
      const aVal = a.fields[sortKey] ?? '';
      const bVal = b.fields[sortKey] ?? '';

      const aNum = parseFloat(aVal);
      const bNum = parseFloat(bVal);
      if (!isNaN(aNum) && !isNaN(bNum)) {
        return sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const comparison = aVal.localeCompare(bVal);
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [incidents, sortKey, sortDirection]);

  const getSeverityClass = (value: string) => {
    const upperVal = value.toUpperCase();
    if (upperVal.includes('SEV 1') || upperVal.includes('SEV1')) return classes.sev1;
    if (upperVal.includes('SEV 2') || upperVal.includes('SEV2')) return classes.sev2;
    if (upperVal.includes('SEV 3') || upperVal.includes('SEV3')) return classes.sev3;
    if (upperVal.includes('SEV 4') || upperVal.includes('SEV4')) return classes.sev4;
    return '';
  };

  const isUrl = (value: string): boolean => {
    return /^https?:\/\//.test(value.trim());
  };

  const renderCellValue = (column: string, value: string, incident: UIIncident) => {
    if (!value) return '-';

    if (column.toLowerCase().includes('sev')) {
      return (
        <Typography className={`${classes.severityCell} ${getSeverityClass(value)}`}>
          {value}
        </Typography>
      );
    }

    if (column === 'Description' || column.toLowerCase().includes('name') || column.toLowerCase().includes('title')) {
      if (incident.spreadsheetRowLink) {
        return (
          <a
            href={incident.spreadsheetRowLink}
            target="_blank"
            rel="noopener noreferrer"
            className={classes.incidentNameLink}
          >
            {value}
          </a>
        );
      }
    }

    if (isUrl(value)) {
      return (
        <a
          href={value}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.linkText}
        >
          {value.length > 50 ? `${value.substring(0, 50)}...` : value}
        </a>
      );
    }

    return value;
  };

  if (incidents.length === 0) {
    return (
      <Box className={classes.container}>
        <Box className={classes.header}>
          <Box>
            <Typography className={classes.title}>All Incidents</Typography>
            <Typography className={classes.subtitle}>
              Detailed list of incidents from the Incident Tracker
            </Typography>
          </Box>
        </Box>
        <Paper className={classes.tableContainer} elevation={0}>
          <Box className={classes.emptyState}>
            <WarningAmberIcon className={classes.emptyIcon} />
            <Typography variant="h6" gutterBottom>
              No incidents found
            </Typography>
            <Typography variant="body2">
              No incidents were recorded for this team in the selected time period.
            </Typography>
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>All Incidents</Typography>
          <Typography className={classes.subtitle}>
            {incidents.length} incident{incidents.length !== 1 ? 's' : ''} from the Incident Tracker
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          className={classes.columnButton}
          onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
          startIcon={<ViewColumnIcon className={classes.columnButtonIcon} />}
        >
          Columns ({selectedColumns.length})
        </Button>
        <Menu
          anchorEl={columnMenuAnchor}
          open={Boolean(columnMenuAnchor)}
          onClose={() => setColumnMenuAnchor(null)}
          PaperProps={{ className: classes.menuPaper }}
        >
          <Box display="flex" justifyContent="space-between" px={2} py={1}>
            <Button size="small" onClick={handleSelectAll}>All</Button>
            <Button size="small" onClick={handleSelectNone}>None</Button>
            <Button size="small" onClick={handleResetToDefault}>Reset</Button>
          </Box>
          <Divider />
          <Typography className={classes.menuHeader}>Available Columns</Typography>
          {allAvailableColumns.map((column) => (
            <MenuItem
              key={column}
              onClick={() => handleColumnToggle(column)}
              className={classes.menuItem}
              dense
            >
              <Checkbox
                checked={selectedColumns.includes(column)}
                size="small"
                sx={{ padding: 0.5 }}
              />
              <ListItemText
                primary={column}
                primaryTypographyProps={{ fontSize: 13 }}
              />
            </MenuItem>
          ))}
        </Menu>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
        <Table>
          <TableHead className={classes.tableHead}>
            <TableRow>
              {selectedColumns.map((column) => (
                <TableCell key={column} className={classes.headerCell}>
                  <TableSortLabel
                    active={sortKey === column}
                    direction={sortKey === column ? sortDirection : 'desc'}
                    onClick={() => handleSort(column)}
                    className={classes.sortLabel}
                  >
                    {column}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedIncidents.map((incident, index) => (
              <TableRow key={index} className={classes.tableRow}>
                {selectedColumns.map((column) => {
                  const isDescriptionColumn = column === 'Description' || column.toLowerCase().includes('name') || column.toLowerCase().includes('title');
                  const cellValue = incident.fields[column] || '';
                  return (
                    <TableCell
                      key={column}
                      className={`${classes.cell} ${column === 'Description' ? classes.descriptionCell : ''}`}
                    >
                      {isDescriptionColumn ? (
                        <TruncatedText text={cellValue}>
                          {renderCellValue(column, cellValue, incident)}
                        </TruncatedText>
                      ) : (
                        <Tooltip title={cellValue} placement="top-start">
                          <span>{renderCellValue(column, cellValue, incident)}</span>
                        </Tooltip>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default IncidentsTable;
